import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Save, Clock, Mail } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { HeroSettingsEditor } from "@/components/admin/HeroSettingsEditor";
import { HeroMediaCleanupCard } from "@/components/admin/HeroMediaCleanupCard";
import { ChatRecipientsCard } from "@/components/admin/ChatRecipientsCard";
import { PipelineRecipientsCard } from "@/components/admin/PipelineRecipientsCard";

export const Route = createFileRoute("/admin/indstillinger")({
  component: AdminSettings,
});

function AdminSettings() {
  const queryClient = useQueryClient();
  const [responseMinutes, setResponseMinutes] = useState<string>("15");
  const [weeklyReportEnabled, setWeeklyReportEnabled] = useState<boolean>(true);
  const [weeklySyncReportEnabled, setWeeklySyncReportEnabled] = useState<boolean>(true);

  const { data: weeklyReportSettings } = useQuery({
    queryKey: ["site-settings", "weekly_email_report"],
    queryFn: async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("*")
        .eq("key", "weekly_email_report")
        .maybeSingle();
      return data;
    },
  });

  const { data: weeklySyncReportSettings } = useQuery({
    queryKey: ["site-settings", "weekly_sync_report"],
    queryFn: async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("*")
        .eq("key", "weekly_sync_report")
        .maybeSingle();
      return data;
    },
  });

  useEffect(() => {
    const v = weeklySyncReportSettings?.value as { enabled?: boolean } | undefined;
    if (typeof v?.enabled === "boolean") setWeeklySyncReportEnabled(v.enabled);
  }, [weeklySyncReportSettings]);

  const saveWeeklySyncReportMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const value = { enabled };
      if (weeklySyncReportSettings) {
        const { error } = await supabase
          .from("site_settings")
          .update({ value })
          .eq("key", "weekly_sync_report");
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("site_settings")
          .insert({ key: "weekly_sync_report", value });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["site-settings", "weekly_sync_report"] });
      toast.success("Ugentlig sync-rapport opdateret");
    },
    onError: () => toast.error("Kunne ikke opdatere ugentlig sync-rapport"),
  });

  useEffect(() => {
    const v = weeklyReportSettings?.value as { enabled?: boolean } | undefined;
    if (typeof v?.enabled === "boolean") setWeeklyReportEnabled(v.enabled);
  }, [weeklyReportSettings]);

  const saveWeeklyReportMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const value = { enabled };
      if (weeklyReportSettings) {
        const { error } = await supabase
          .from("site_settings")
          .update({ value })
          .eq("key", "weekly_email_report");
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("site_settings")
          .insert({ key: "weekly_email_report", value });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["site-settings", "weekly_email_report"] });
      toast.success("Ugentlig rapport opdateret");
    },
    onError: () => toast.error("Kunne ikke opdatere ugentlig rapport"),
  });

  const { data: responseSettings } = useQuery({
    queryKey: ["site-settings", "response_time"],
    queryFn: async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("*")
        .eq("key", "response_time")
        .maybeSingle();
      return data;
    },
  });

  useEffect(() => {
    const v = responseSettings?.value as { minutes?: number } | undefined;
    if (typeof v?.minutes === "number") setResponseMinutes(String(v.minutes));
  }, [responseSettings]);

  const saveResponseMutation = useMutation({
    mutationFn: async () => {
      const minutes = Math.max(1, Math.min(720, Number.parseInt(responseMinutes, 10) || 15));
      const value = { minutes };
      if (responseSettings) {
        const { error } = await supabase
          .from("site_settings")
          .update({ value })
          .eq("key", "response_time");
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("site_settings")
          .insert({ key: "response_time", value });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["site-settings", "response_time"] });
      toast.success("Svartid gemt!");
    },
    onError: () => toast.error("Kunne ikke gemme svartid"),
  });

  // Hero settings are managed by HeroSettingsEditor (one tab per page).

  return (
    <div>
      <h1 className="text-2xl font-bold text-foreground">Indstillinger</h1>
      <p className="mt-1 text-sm text-muted-foreground">Administrer forsiden og andre indstillinger</p>

      <div className="mt-8">
        <HeroSettingsEditor />
      </div>

      <HeroMediaCleanupCard />

      <div className="mt-8 max-w-2xl rounded-xl bg-card p-8 shadow-sm ring-1 ring-border">
        <div className="flex items-center gap-2">
          <Clock size={18} className="text-accent" />
          <h2 className="text-lg font-semibold text-foreground">Svartid</h2>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Hvor mange minutter står der typisk i åbningstiden ("Åbent nu · vi svarer typisk inden for X min."). Vises på kontaktside, bilside og i chat-widgetten.
        </p>

        <div className="mt-6 max-w-xs">
          <label className="text-sm font-medium text-foreground">Minutter</label>
          <Input
            type="number"
            min={1}
            max={720}
            value={responseMinutes}
            onChange={(e) => setResponseMinutes(e.target.value)}
            className="mt-1.5"
          />
          <p className="mt-1.5 text-xs text-muted-foreground">Mellem 1 og 720 minutter.</p>
        </div>

        <Button
          onClick={() => saveResponseMutation.mutate()}
          disabled={saveResponseMutation.isPending}
          className="mt-6 bg-accent text-accent-foreground hover:brightness-110"
        >
          <Save size={16} className="mr-2" />
          {saveResponseMutation.isPending ? "Gemmer..." : "Gem svartid"}
        </Button>
      </div>

      <ChatRecipientsCard />

      <PipelineRecipientsCard />

      <div className="mt-8 max-w-2xl rounded-xl bg-card p-8 shadow-sm ring-1 ring-border">
        <div className="flex items-center gap-2">
          <Mail size={18} className="text-accent" />
          <h2 className="text-lg font-semibold text-foreground">Ugentlig email-rapport</h2>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Send en automatisk ugentlig oversigt til admins hver mandag kl. 08:00 dansk tid.
          Indeholder fail-rate, top fejlende templates og DLQ-status for de sidste 7 dage.
        </p>

        <div className="mt-6 flex items-center justify-between rounded-lg border border-border bg-muted/30 p-4">
          <div>
            <p className="text-sm font-medium text-foreground">
              {weeklyReportEnabled ? "Aktiveret" : "Deaktiveret"}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Modtagere: alle admins og managers
            </p>
          </div>
          <Switch
            checked={weeklyReportEnabled}
            onCheckedChange={(checked) => {
              setWeeklyReportEnabled(checked);
              saveWeeklyReportMutation.mutate(checked);
            }}
            disabled={saveWeeklyReportMutation.isPending}
          />
        </div>
      </div>

      <div className="mt-8 max-w-2xl rounded-xl bg-card p-8 shadow-sm ring-1 ring-border">
        <div className="flex items-center gap-2">
          <Mail size={18} className="text-accent" />
          <h2 className="text-lg font-semibold text-foreground">Ugentlig sync-rapport</h2>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Send en automatisk ugentlig sync-health rapport til admins hver mandag kl. 08:00 dansk tid.
          Indeholder antal kørsler, succes-rate, gennemsnitligt antal biler i feed og evt. anomalier de sidste 7 dage.
        </p>

        <div className="mt-6 flex items-center justify-between rounded-lg border border-border bg-muted/30 p-4">
          <div>
            <p className="text-sm font-medium text-foreground">
              {weeklySyncReportEnabled ? "Aktiveret" : "Deaktiveret"}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Modtagere: alle admins og managers
            </p>
          </div>
          <Switch
            checked={weeklySyncReportEnabled}
            onCheckedChange={(checked) => {
              setWeeklySyncReportEnabled(checked);
              saveWeeklySyncReportMutation.mutate(checked);
            }}
            disabled={saveWeeklySyncReportMutation.isPending}
          />
        </div>
      </div>
    </div>
  );
}
